import { toast } from "react-hot-toast";

export const extractErrorMessage = (error: unknown): string => {
  if (!error) return "An unexpected error occurred";

  let raw = "";

  if (typeof error === "string") {
    raw = error;
  } else if (typeof error === "object") {
    const errObj = error as Record<string, unknown>;
    if (
      errObj.body &&
      typeof (errObj.body as Record<string, unknown>).message === "string"
    ) {
      raw = (errObj.body as Record<string, unknown>).message as string;
    } else if (typeof errObj.message === "string") {
      raw = errObj.message;
    } else if (errObj.error) {
      return extractErrorMessage(errObj.error);
    } else {
      try {
        raw = JSON.stringify(error);
      } catch {
        raw = String(error);
      }
    }
  } else {
    raw = String(error);
  }

  raw = raw.trim();

  // If raw string is JSON (e.g., '{"message":"Operation timed out: handle-receive",...}')
  if (raw.startsWith("{") && raw.endsWith("}")) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.message === "string") {
        raw = parsed.message;
      }
    } catch {
      // Ignore JSON parse error, keep raw
    }
  }

  // Strip leading "Error: " if present
  if (raw.startsWith("Error: ")) {
    raw = raw.slice(7).trim();
  }

  // Handle specific known patterns
  const lower = raw.toLowerCase();
  if (
    lower.includes("operation timed out") ||
    lower.includes("handle-receive") ||
    lower.includes("took too long") ||
    lower.includes("timed out") ||
    lower.includes("timeout")
  ) {
    return "Request timed out. Please try again.";
  }

  if (
    lower.includes("unauthorized") ||
    lower.includes("permission") ||
    lower.includes("forbidden")
  ) {
    return "Access denied. You do not have permission for this request.";
  }

  if (
    lower.includes("networkerror") ||
    lower.includes("failed to fetch") ||
    lower.includes("network request failed") ||
    lower.includes("load failed")
  ) {
    return "Network connection error. Please check your connection.";
  }

  return raw || "An unexpected error occurred";
};

export const logErrorToConsole = (error: unknown, context?: string) => {
  const prefix = context ? `[ViewChat] ${context}:` : "[ViewChat Error]:";
  console.error(prefix, error);
};

const capturePostHogError = (rawMessage: string, context?: string) => {
  try {
    const posthog = (globalThis as Record<string, unknown>)._posthog as
      | {
        capture: (
          event: string,
          properties?: Record<string, unknown>,
        ) => void;
      }
      | undefined;
    posthog?.capture("Application Error", {
      message: rawMessage,
      context,
      url: typeof globalThis.location !== "undefined"
        ? globalThis.location.href
        : "",
    });
  } catch {
    // Ignore analytics capture errors
  }
};

export const notifyError = (
  error: unknown,
  context?: string,
  options?: { id?: string; duration?: number },
): string => {
  logErrorToConsole(error, context);

  const cleanMessage = extractErrorMessage(error);
  const displayMessage = context ? `${context}: ${cleanMessage}` : cleanMessage;

  capturePostHogError(displayMessage, context);

  const toastId = options?.id ||
    (context ? `${context}:${cleanMessage}` : cleanMessage);
  toast.error(displayMessage, {
    id: toastId,
    duration: options?.duration ?? 4000,
  });

  return displayMessage;
};

export const notifySuccess = (
  message: string,
  options?: { id?: string; duration?: number },
) => {
  toast.success(message, options);
};

export const notifyInfo = (
  message: string,
  options?: { id?: string; duration?: number },
) => {
  toast(message, options);
};
